import axios from 'axios';
import { APP_NETWORK_EVENT, emitRuntimeEvent } from '../utils/runtimeEvents';
import Analytics from '../utils/analytics';

// Detectar se está em produção ou desenvolvimento
const isProduction = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';

// URL base da API
// Em produção: usa URL relativa (nginx faz proxy de /api para localhost:5003)
// Em desenvolvimento: usa localhost:5003
const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (isProduction) {
    // Em produção, usa URL relativa (mesmo domínio)
    return '/api';
  }

  // Em desenvolvimento, usa localhost
  return 'http://localhost:5003/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000, // 30 segundos de timeout
  timeoutErrorMessage: 'Tempo de requisição excedido. Tente novamente.'
});

let lastConnectivityEventAt = 0;

const isMutationMethod = (method) => ['post', 'put', 'patch', 'delete'].includes(String(method || 'get').toLowerCase());

const emitConnectivityWarning = (detail) => {
  const now = Date.now();
  if ((now - lastConnectivityEventAt) < 3000) {
    return;
  }

  lastConnectivityEventAt = now;
  emitRuntimeEvent(APP_NETWORK_EVENT, detail);
};

const createConnectivityError = (message, code) => {
  const error = new Error(message);
  error.code = code;
  error.isConnectivityError = true;
  error.response = {
    status: 0,
    data: {
      message,
      code
    }
  };
  return error;
};

// Interceptor para adicionar token
api.interceptors.request.use(
  (config) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine && isMutationMethod(config.method)) {
      const message = 'Sem conexao. Essa acao precisa de internet e nao foi enviada.';
      emitConnectivityWarning({
        state: 'offline',
        message,
        code: 'OFFLINE_ACTION_BLOCKED'
      });
      return Promise.reject(createConnectivityError(message, 'OFFLINE_ACTION_BLOCKED'));
    }
    // Obter token mais recente do localStorage antes de cada requisição
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    if (!error.response) {
      const offline = typeof navigator !== 'undefined' && !navigator.onLine;
      const isTimeout = error.code === 'ECONNABORTED';
      const message = offline
        ? 'Voce esta offline. Reconecte-se para carregar ou salvar dados.'
        : isTimeout
          ? 'A conexao esta lenta ou instavel. Tente novamente.'
          : 'Falha de rede ao comunicar com o servidor. Tente novamente.';

      emitConnectivityWarning({
        state: offline ? 'offline' : 'unstable',
        message,
        code: offline ? 'OFFLINE' : (isTimeout ? 'NETWORK_TIMEOUT' : 'NETWORK_ERROR')
      });

      error.response = {
        status: 0,
        data: {
          message,
          code: offline ? 'OFFLINE' : (isTimeout ? 'NETWORK_TIMEOUT' : 'NETWORK_ERROR')
        }
      };
    }
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Erro 401 - Não autorizado (token inválido/expirado/revogado)
    if (error.response?.status === 401) {
      const errorCode = error.response?.data?.code;

      // Limpar token e dados relacionados
      localStorage.removeItem('token');
      localStorage.removeItem('token_expires_at');

      // Se token foi revogado ou expirado, redirecionar para login
      if (errorCode === 'TOKEN_REVOKED' || errorCode === 'TOKEN_EXPIRED') {
        // Evitar redirecionamento em loop
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else {
        // Token inválido - também redirecionar
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    // Erro 403 - Acesso negado
    if (error.response?.status === 403) {
      // Não redirecionar, apenas rejeitar para que o componente trate
      console.warn('Acesso negado:', error.response?.data?.message);
    }

    // Erros de servidor: métrica apenas — mensagem fica com a tela (evita toast genérico + duplicidade)
    if (error.response?.status >= 500) {
      const serverCode = error.response?.status;
      Analytics.trackError('Axios API', `Server Error ${serverCode}`);
    }

    return Promise.reject(error);
  }
);

export default api;
